import CloseIcon from '@mui/icons-material/Close';
import { Alert, IconButton, Snackbar, Stack } from '@mui/material';
import { useEffect } from 'react';

import { useAppDispatch } from '#shared';

import {
  notificationActions,
  notificationSelectors,
} from '../model/notification.slice';

/**
 * NotificationSnackbar Component
 *
 * Displays notifications using MUI Snackbar and Alert components.
 * Manages multiple notifications in a stack layout.
 *
 * @example
 * // Add to your App component:
 * import { NotificationSnackbar } from '#features';
 *
 * function App() {
 *   return (
 *     <>
 *       <YourAppContent />
 *       <NotificationSnackbar />
 *     </>
 *   );
 * }
 *
 * // Then use anywhere in your app:
 * import { useDispatch } from 'react-redux';
 * import { notificationActions } from '#features';
 *
 * function MyComponent() {
 *   const dispatch = useDispatch();
 *
 *   const handleSuccess = () => {
 *     dispatch(notificationActions.showNotification({
 *       message: 'Operation completed successfully!',
 *       severity: 'success',
 *     }));
 *   };
 *
 *   const handleError = () => {
 *     dispatch(notificationActions.showNotification({
 *       message: 'An error occurred',
 *       severity: 'error',
 *       autoHideDuration: 10000, // Optional: custom duration
 *     }));
 *   };
 * }
 */
export const NotificationSnackbar = () => {
  const dispatch = useAppDispatch();
  const notifications = notificationSelectors.useSelectNotifications();

  const handleClose = (id: string) => {
    dispatch(notificationActions.hideNotification(id));
  };

  // Remove notification from state after it closes
  useEffect(() => {
    const closedNotifications = notifications.filter(n => !n.open);
    closedNotifications.forEach(notification => {
      const timer = setTimeout(() => {
        dispatch(notificationActions.removeNotification(notification.id));
      }, 300); // Wait for closing animation

      return () => clearTimeout(timer);
    });
  }, [notifications, dispatch]);

  return (
    <Stack spacing={1} sx={{ position: 'fixed', bottom: 24, right: 24 }}>
      {notifications.map(notification => (
        <Snackbar
          key={notification.id}
          open={notification.open}
          autoHideDuration={notification.autoHideDuration}
          onClose={() => handleClose(notification.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ position: 'relative' }}
        >
          <Alert
            severity={notification.severity}
            variant="filled"
            onClose={() => handleClose(notification.id)}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => handleClose(notification.id)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              width: '100%',
              minWidth: 300,
              maxWidth: 500,
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </Stack>
  );
};
